import type {Container} from '../container.ts';
import {FetchRetryer} from '../fetchretryer.ts';
import {BACKGROUND_LAYER} from '../allspice_app/stores/layers.ts';

/**
 * @typedef {string} SvgGroupId
 */

/**
 * Deep cloned symbol group and style element, or null if it wasn't found.
 */
export function extractSvgGroup(sourceEl: SVGSVGElement, groupId: string): {symbolGroup: SVGGElement, style: SVGStyleElement | null} | null {
  /** @type {SVGElement | HTMLElement | null} */
  const sourceSymbolGroup = sourceEl.querySelector(`:scope > #${groupId}`);
  if (!sourceSymbolGroup) {
    return null;
  }
  if (!(sourceSymbolGroup instanceof SVGElement) ||
    sourceSymbolGroup.tagName !== 'g') {
    throw new Error(`Component element found in SVG isn't a group: ${groupId}; sourceEl.tagName=${sourceSymbolGroup.tagName}`);
  }
  const symbolGroup =
    sourceSymbolGroup.cloneNode(true) as SVGGElement;
  // It may have a transform to display the component shifted down below other
  // components.
  symbolGroup.removeAttribute('transform');

  // The element immediately before the group is the style element.
  const sourceStyle = sourceSymbolGroup.previousElementSibling;
  /** @type {SVGStyleElement | null} */
  let style = null;
  // Don't return a non-style element.
  if (sourceStyle instanceof Element && sourceStyle.tagName === 'style') {
    style = sourceStyle.cloneNode(true) as SVGStyleElement;
  }

  return {symbolGroup, style};
}

export function cssForVariant(selector: string, variantId: string): string {
  if (variantId === 'default') {
    return '';
  }

  const styles = [];
  const cssEscaped = CSS.escape(variantId);

  // We need the important here to prepend the style, since the SVG itself has default styles
  styles.push(`${selector} [data-variant="${cssEscaped}"] { visibility: visible !important; }`, `${selector} [data-variant]:not([data-variant="${cssEscaped}"]) { visibility: hidden !important; }`, `${selector} [data-hide-for-variants~="${cssEscaped}"] { visibility: hidden !important; }`, `${selector} [data-not-fitted-for-variants~="${cssEscaped}"] { opacity: 0.2 !important; }`);

  return styles.join('\n');
}

export function parseSvgText(svgText: string): SVGSVGElement {
  // parsing the SVG using innerHTML is (sometimes quite a bit) faster than using DOMParser#parseFromString
  const div = document.createElement('div');
  div.innerHTML = svgText;

  if (!(div.firstElementChild && div.firstElementChild instanceof SVGSVGElement)) {
    return null;
  }

  const sharedSvgEl = div.firstElementChild as SVGSVGElement;
  return sharedSvgEl;
}

const PREAMBLE_REGEXP = /^<svg.+?>/;

export class SVG {
  _svg: SVGSVGElement | null;
  _blob: Blob | null;
  _text: string | null;
  _newCSS: string[];
  _url: string | null;
  key: string;
  viewBox: {height: number, width: number, x: number, y: number} | null;
  id: string;
  el: Element;
  children: Blob | null;
  refs: number;
  cleanup: (() => void) | null;

  static async fetch(container: Container, url: string, {
    prefix = 'id',
    transform = function(svg: SVG): SVG { return svg },
  } = {}): Promise<SVG> {
    const cache = container.cacheFor('SVG');
    const key = `${prefix}:${url}`;

    const cached = cache.get(key);
    if (cached !== undefined) {
      const resp = await cached;
      resp.refs++;
      return resp;
    }

    const req = SVG.uncachedFetch(container, url, transform);
    cache.set(key, req);
    const svg = await req;
    svg.cleanup = () => {
      cache.delete(key);
    };
    return svg;
  }

  static async uncachedFetch(container: Container, url: string, transform: (svg: SVG) => SVG | Promise<SVG> = function(svg: SVG): SVG { return svg }): Promise<SVG> {
    const fetch = FetchRetryer.for(container, url, {skipCache: true, fetchImmediately: true});
    const retry = await fetch.promise;
    const svgBlob = await retry.response.blob();
    const svg = await SVG.fromBlob(svgBlob);
    const transformedSvg = await transform(svg);
    return transformedSvg;
  }

  static async fromBlob(blob: Blob): Promise<SVG> {
    // Only parse the first 128kB of a document. This avoids large memory allocations
    const pre = await blob.slice(0, 128 * 1024).text();
    const svg = new SVG(pre, blob);
    return svg;
  }

  static fromElement(el: SVGSVGElement): SVG {
    const svg = new SVG(null, null);
    svg._svg = el;
    svg.viewBox = {
      height: el.viewBox.baseVal.height,
      width: el.viewBox.baseVal.width,
      x: el.viewBox.baseVal.x,
      y: el.viewBox.baseVal.y,
    };
    svg.id = el.id;
    return svg;
  }

  static async fromString(string: string): Promise<SVG> {
    const blob = new Blob([string], {type: 'text/xml'});
    return SVG.fromBlob(blob);
  }

  static parsePreamble(svgString: string): string | null {
    const preamble = svgString.match(PREAMBLE_REGEXP);
    if (preamble.length > 0) {
      return `${preamble[0]}`;
    }
    return null;
  }

  constructor(svgString: string | null, blob: Blob | null) {
    if (svgString === null) {
      return;
    }

    this.refs = 0;
    this._text = svgString;
    this._blob = blob;
    this._newCSS = [];
    const preamble = SVG.parsePreamble(svgString);
    if (preamble) {
      const el = parseSvgText(`${preamble}</svg>`);

      this.viewBox = {
        height: el.viewBox.baseVal.height,
        width: el.viewBox.baseVal.width,
        x: el.viewBox.baseVal.x,
        y: el.viewBox.baseVal.y,
      };

      this.id = el.id;
      this.el = el;
      this.children = blob.slice(preamble.length, -6);
    }
  }

  async svg() {
    if (this._svg) {
      return this._svg;
    }

    if (this._blob) {
      const text = await this._blob.text();

      // this._svg = parseSvgText(text);
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/xml');
      this._svg = doc.firstChild as SVGSVGElement;

      this._blob = null;
      this.children = null;
      this._text = null;
      if (this._newCSS) {
        this.prependStyle(...this._newCSS);
        this._newCSS = [];
      }

      return this._svg;
    }

    throw new Error('SVG already consumed');
  }

  prependStyle(...newCSS: string[]): void {
    if (this._svg) {
      for (const css of newCSS) {
        this._svg.querySelector('style').insertAdjacentText('afterbegin', css);
      }
      return;
    }

    if (this._blob) {
      this._newCSS.push(...newCSS);
      return;
    }

    throw new Error('SVG already consumed');
  }

  url() {
    if (this._url) {
      return this._url;
    }

    if (this._blob) {
      let blob = this._blob;
      if (this._newCSS) {
        // Find open style tags. We need a mutable regexp here.
        const regexp = /<style.+>/g;
        const match = regexp.exec(this._text);
        if (!match) {
          throw new Error('unable to find style tag');
        }
        // The style tag opens after the lastIndex plus the length of the match.
        const styleIndex = regexp.lastIndex + match.length;
        const before = blob.slice(0, styleIndex);
        const after = blob.slice(styleIndex);
        // Insert the new CSS at the beginning of the style tag
        blob = new Blob([before, ...this._newCSS, after], {
          type: 'image/svg+xml',
        });
      }
      this._blob = null;
      this.children = null;
      this._url = URL.createObjectURL(blob);
      return this._url;
    }

    if (this._svg) {
      const blob = new Blob([this._svg.outerHTML], {
        type: 'image/svg+xml',
      });
      this._svg = null;
      this._url = URL.createObjectURL(blob);
      return this._url;
    }

    throw new Error('SVG already consumed');
  }

  release() {
    this.refs--;
    if (this.refs > 0) {
      return;
    }

    if (this._url) {
      URL.revokeObjectURL(this._url);
      this._url = null;
    }

    if (this._svg) {
      this._svg = null;
    }

    if (this._blob) {
      this._blob = null;
    }

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }
}

export async function transformForSnippet(svgDoc: SVG, {layerIds = null as string | null, variant = null as string | null, targetDocId = ''}): Promise<SVG> {
  if ((layerIds === null || layerIds === undefined) &&
        (variant === null || variant === undefined || variant === 'default') &&
        (targetDocId === '')) {
      // Visible layers, non-default variant not set, no sub document.  Show them all.
    return svgDoc;
  }

  if (targetDocId !== '') {
    const el = await svgDoc.svg();
    const destDoc = el.ownerDocument;
    const svgEl = destDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const className = el.getAttribute('class');
    if (className) {
      svgEl.setAttribute('class', className);
    }
      // Scale based on the container.
    svgEl.setAttribute('width', '100%');

    const groupId = `${el.id}-${targetDocId}`;
      // extract the group by building the full group id
    const {symbolGroup, style} = extractSvgGroup(el, groupId);

    if (style) {
      svgEl.append(style);
    }
    svgEl.append(symbolGroup);

      // Extract the viewBox from the group element.  We don't use the width and
      // height so that we can scale it based on the container.
    svgEl.setAttribute('viewBox', symbolGroup.getAttribute('data-view-box') ?? '');

    const newSVG = SVG.fromElement(svgEl);
    if (variant !== null && variant !== undefined) {
        // Append new css to the style
      newSVG.prependStyle(cssForVariant(`#${groupId}`, variant));
    }
    return newSVG;
  }

  if (!svgDoc.id) {
    console.error("svg doc id not found in snippet response; I can't set layer visibility or variant");
    return svgDoc;
  }

  const docId = CSS.escape(svgDoc.id);

  let newCss = '';

  if (layerIds !== null && layerIds !== undefined) {
    newCss = `#${docId} [data-layer-id] { display: none; }\n`;
    const layerIdArray = layerIds
      .split(',')
      .filter((id: string) => id !== '');
    if (layerIdArray.length) {
      newCss += layerIdArray.map((layerId: string) => {
        return `#${docId} [data-layer-id="${CSS.escape(layerId)}"]`;
      }).join(',');
      newCss += ' { display: inline; }';
    }
  }

    // always show the background (won't be available in layer dropdown)
  newCss += `#${docId} [data-layer-id="${CSS.escape(BACKGROUND_LAYER.id.toString())}"] { display: inline; }\n`;

  if (variant !== null && variant !== undefined) {
    newCss += cssForVariant(`#${docId}`, variant);
  }

    // Append new css to the style
  svgDoc.prependStyle(newCss);

  return svgDoc;
}
